(() => {
  const READY_EVENT = 'wishlist-hero-wishlist-sdk-ready';
  const BUTTON_SELECTOR = '[data-wishlist-button]';
  const DRAWER_SELECTOR = 'wishlist-drawer';
  const HEADER_SELECTOR = '[data-wishlist-trigger]';
  const COUNT_SELECTOR = '[data-wishlist-count]';
  const LEGACY_UI_SELECTOR = [
    '#wishlisthero-product-page-button-container',
    '#wishlist-hero-product-page-button',
    '.product__info-container .wishlist-hero-custom-button:not([data-wishlist-button])',
    '.product__info-container [data-wlh-id]:not([data-wishlist-button])',
    '.product__info-container button[aria-label*="WUNSCHLISTE"]:not([data-wishlist-button])',
    '.product__info-container button[aria-label*="Wunschliste"]:not([data-wishlist-button])',
  ].join(',');

  const methodNames = {
    get: ['GetWishListItems', 'GetWishListItemsFromServer', 'getItems', 'getWishlistItems', 'getWishlist', 'getWishList', 'fetchItems', 'fetchWishlist', 'getUserWishlist', 'all'],
    add: ['AddWishListItem', 'addItem', 'addToWishlist', 'addToWishList', 'addProduct', 'add', 'createItem'],
    remove: ['DeleteWishListItem', 'removeItem', 'removeFromWishlist', 'removeFromWishList', 'deleteItem', 'deleteProduct', 'remove', 'delete'],
  };

  const strings = (() => {
    const script = document.querySelector('[data-wishlist-strings]');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[wishlist] Unable to parse strings', error);
      return {};
    }
  })();

  const state = {
    sdk: null,
    ready: false,
    items: [],
    buttons: new Set(),
    pending: new WeakSet(),
  };

  const normalizeId = (value) => String(value || '').replace(/^gid:\/\/shopify\/(?:Product|ProductVariant)\//, '');
  const compact = (value) => (value === null || value === undefined ? '' : String(value).trim());
  const escapeHtml = (value) => compact(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  const escapeAttribute = escapeHtml;
  const money = (value) => {
    if (!value) return '';
    if (/[^\d.,]/.test(String(value))) return String(value);

    const number = Number(String(value).replace(',', '.'));
    if (Number.isNaN(number)) return String(value);

    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    }).format(number);
  };
  const absoluteUrl = (value) => {
    if (!value) return '';

    try {
      return new URL(value, window.location.origin).href;
    } catch (error) {
      return value;
    }
  };
  const protocolRelativeUrl = (value) => absoluteUrl(value).replace(/^https?:/, '');

  const debug = (...args) => {
    if (window.wishlistHeroCustomDebug) console.log('[wishlist]', ...args);
  };

  const toast = (type, title, message = '') => {
    window.themeToast?.show({
      type,
      title,
      message,
    });
  };

  const getSdkCandidates = (event) => [
    event?.detail?.sdk,
    event?.detail?.wishlist,
    event?.detail?.api,
    event?.detail,
    window.WishListHero_SDK,
    window.WishlistHero,
    window.WishListHero,
    window.wishlistHero,
    window.WishlistHeroSDK,
    window.WishListHeroSDK,
    window.wishlistHeroSDK,
  ].filter((candidate) => candidate && typeof candidate === 'object');

  const findMethod = (sdk, type) => {
    for (const name of methodNames[type]) {
      if (typeof sdk[name] === 'function') return sdk[name].bind(sdk);
      if (sdk.wishlist && typeof sdk.wishlist[name] === 'function') return sdk.wishlist[name].bind(sdk.wishlist);
      if (sdk.api && typeof sdk.api[name] === 'function') return sdk.api[name].bind(sdk.api);
    }

    return null;
  };

  const getPayload = (buttonOrItem) => ({
    id: compact(buttonOrItem.dataset?.wishlistProductId || buttonOrItem.productId || buttonOrItem.id),
    productId: compact(buttonOrItem.dataset?.wishlistProductId || buttonOrItem.productId || buttonOrItem.id),
    product_id: compact(buttonOrItem.dataset?.wishlistProductId || buttonOrItem.productId || buttonOrItem.id),
    variantId: compact(buttonOrItem.dataset?.wishlistVariantId || buttonOrItem.variantId),
    variant_id: compact(buttonOrItem.dataset?.wishlistVariantId || buttonOrItem.variantId),
    handle: compact(buttonOrItem.dataset?.wishlistHandle || buttonOrItem.handle),
    title: compact(buttonOrItem.dataset?.wishlistTitle || buttonOrItem.title),
    name: compact(buttonOrItem.dataset?.wishlistTitle || buttonOrItem.title || buttonOrItem.name),
    url: compact(buttonOrItem.dataset?.wishlistUrl || buttonOrItem.url),
    link: compact(buttonOrItem.dataset?.wishlistUrl || buttonOrItem.url || buttonOrItem.link),
    image: compact(buttonOrItem.dataset?.wishlistImage || buttonOrItem.image),
    price: compact(buttonOrItem.dataset?.wishlistPrice || buttonOrItem.price),
    formattedPrice: compact(buttonOrItem.dataset?.wishlistFormattedPrice || buttonOrItem.formattedPrice),
  });

  const getWishlistHash = async (createIfMissing = false) => {
    if (typeof state.sdk?.GetWishListHash !== 'function') return '';
    return compact(await state.sdk.GetWishListHash(createIfMissing));
  };

  const getCustomerId = async () => {
    if (typeof state.sdk?.GetCustomerId !== 'function') return '';
    return compact(await state.sdk.GetCustomerId());
  };

  const getWishlistHeroPayload = async (payload, action) => {
    const wishlistHash = await getWishlistHash(action === 'add');
    const variantId = Number(payload.variantId || payload.variant_id);

    if (action === 'remove') {
      return {
        ProductVariantId: variantId || payload.variantId || payload.variant_id,
        WishlistHash: wishlistHash,
      };
    }

    const customerId = await getCustomerId();
    const productId = Number(payload.productId || payload.product_id || payload.id);
    const price = Number(payload.price);

    return {
      ...(customerId ? { CustomerId: Number(customerId) || customerId } : {}),
      ProductId: productId || payload.productId || payload.product_id || payload.id,
      ProductTitle: payload.title || payload.name,
      ProductPrice: Number.isNaN(price) ? payload.price : price,
      ProductLink: absoluteUrl(payload.url || payload.link),
      ProductVariantId: variantId || payload.variantId || payload.variant_id,
      ProductImage: protocolRelativeUrl(payload.image),
      WishlistHash: wishlistHash,
    };
  };

  const callSdk = async (type, payload = {}) => {
    if (!state.sdk) throw new Error('Wishlist Hero SDK is not ready');

    if (type === 'get' && typeof state.sdk.GetWishListItems === 'function') {
      return state.sdk.GetWishListItems();
    }

    if (type === 'get' && typeof state.sdk.GetWishListItemsFromServer === 'function') {
      return state.sdk.GetWishListItemsFromServer();
    }

    if (type === 'add' && typeof state.sdk.AddWishListItem === 'function') {
      return state.sdk.AddWishListItem(await getWishlistHeroPayload(payload, 'add'));
    }

    if (type === 'remove' && typeof state.sdk.DeleteWishListItem === 'function') {
      return state.sdk.DeleteWishListItem(await getWishlistHeroPayload(payload, 'remove'));
    }

    const method = findMethod(state.sdk, type);
    if (!method) {
      console.warn('[wishlist] Wishlist Hero SDK method not found', {
        type,
        sdk: state.sdk,
        availableKeys: Object.keys(state.sdk),
      });
      throw new Error('Wishlist Hero API method is not available');
    }

    if (type === 'get') return method();

    const wishlistHeroPayload = await getWishlistHeroPayload(payload, type);
    const attempts = [
      [wishlistHeroPayload],
      [payload],
      [payload.productId, payload.variantId, payload],
      [payload.product_id, payload.variant_id, payload],
    ];

    let lastError = null;
    for (const args of attempts) {
      try {
        return await method(...args);
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Wishlist Hero API call failed');
  };

  const normalizeItem = (item) => {
    const source = item?.product || item?.item || item;
    const variant = item?.variant || source?.variant || {};
    const productId = normalizeId(source?.ProductId || source?.ProductID || source?.productId || source?.product_id || source?.id || item?.ProductId || item?.ProductID || item?.productId || item?.product_id);
    const variantId = normalizeId(
      variant?.id ||
      variant?.ProductVariantId ||
      variant?.ProductVariantID ||
      variant?.VariantId ||
      variant?.VariantID ||
      variant?.variantId ||
      variant?.variant_id ||
      source?.ProductVariantId ||
      source?.ProductVariantID ||
      source?.VariantId ||
      source?.VariantID ||
      source?.productVariantId ||
      source?.product_variant_id ||
      source?.variantId ||
      source?.variant_id ||
      item?.ProductVariantId ||
      item?.ProductVariantID ||
      item?.VariantId ||
      item?.VariantID ||
      item?.productVariantId ||
      item?.product_variant_id ||
      item?.variantId ||
      item?.variant_id
    );

    return {
      raw: item,
      productId,
      variantId,
      handle: compact(source?.handle || item?.handle),
      title: compact(source?.ProductName || source?.ProductTitle || source?.title || source?.name || item?.ProductName || item?.ProductTitle || item?.title || item?.name),
      url: compact(source?.ProductLink || source?.url || source?.link || item?.ProductLink || item?.url || item?.link),
      image: compact(source?.ProductImage || source?.image || source?.imageUrl || source?.image_url || item?.ProductImage || item?.image || item?.imageUrl || item?.image_url),
      price: compact(source?.Price || source?.ProductPrice || source?.price || item?.Price || item?.ProductPrice || item?.price),
      formattedPrice: compact(source?.formattedPrice || source?.formatted_price || item?.formattedPrice || item?.formatted_price),
    };
  };

  const normalizeItems = (response) => {
    const list =
      response?.items ||
      response?.wishlistItems ||
      response?.wishlist?.items ||
      response?.data?.items ||
      response?.data ||
      response ||
      [];

    return Array.isArray(list) ? list.map(normalizeItem).filter((item) => item.productId || item.variantId || item.title) : [];
  };

  const sameProduct = (item, payload) => {
    const productMatch = item.productId && normalizeId(payload.productId) && item.productId === normalizeId(payload.productId);
    const variantMatch = item.variantId && normalizeId(payload.variantId) && item.variantId === normalizeId(payload.variantId);
    return variantMatch || productMatch;
  };

  const updateCounts = () => {
    const count = state.items.length;

    document.querySelectorAll(COUNT_SELECTOR).forEach((element) => {
      element.textContent = count > 99 ? '99+' : String(count);
      element.classList.toggle('wishlist-hero-items-count-exists', count > 0);
      element.hidden = count === 0;
    });

    document.querySelectorAll(HEADER_SELECTOR).forEach((element) => {
      element.classList.toggle('has-items', count > 0);
      element.setAttribute('aria-label', `${strings.title || 'Wishlist'}${count ? ` (${count})` : ''}`);
    });

    document.querySelectorAll('[data-wishlist-count-label]').forEach((element) => {
      element.textContent = (strings.count || '([count])').replace('[count]', count);
      element.hidden = count === 0;
    });
  };

  const updateButtons = () => {
    state.buttons.forEach((button) => {
      if (!button.isConnected) {
        state.buttons.delete(button);
        return;
      }

      const payload = getPayload(button);
      const active = state.items.some((item) => sameProduct(item, payload));

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? strings.remove || 'Remove from wishlist' : strings.add || 'Add to wishlist');
      button.title = active ? strings.remove || 'Remove from wishlist' : strings.add || 'Add to wishlist';
    });
  };

  const renderDrawer = () => {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;

    const container = drawer.querySelector('[data-wishlist-items]');
    const cartableItems = state.items.filter((item) => item.variantId);
    drawer.classList.toggle('is-empty', state.items.length === 0);
    drawer.querySelectorAll('[data-wishlist-cart-add-all]').forEach((button) => {
      button.disabled = !cartableItems.length;
      button.textContent = strings.addAllToCart || 'Add all to cart';
    });

    if (!container) return;

    container.innerHTML = state.items.map((item, index) => {
      const title = item.title || strings.viewProduct || 'Product';
      const safeTitle = escapeHtml(title);
      const safeTitleAttribute = escapeAttribute(title);
      const safeUrl = escapeAttribute(item.url || '#');
      const safeImage = escapeAttribute(item.image);
      const image = item.image
        ? `<a class="wishlist-drawer__image" href="${safeUrl}"><img src="${safeImage}" alt="${safeTitleAttribute}" loading="lazy"></a>`
        : `<a class="wishlist-drawer__image" href="${safeUrl}" aria-label="${safeTitleAttribute}"></a>`;
      const price = escapeHtml(item.formattedPrice || money(item.price));

      return `
        <article class="wishlist-drawer__item" data-wishlist-drawer-item="${index}">
          ${image}
          <div class="wishlist-drawer__details">
            <a class="wishlist-drawer__title" href="${safeUrl}">${safeTitle}</a>
            ${price ? `<div class="wishlist-drawer__price">${price}</div>` : ''}
            <div class="wishlist-drawer__actions">
              ${item.variantId ? `<button class="button wishlist-drawer__add" type="button" data-wishlist-cart-add data-index="${index}">${escapeHtml(strings.addToCart || 'Add to cart')}</button>` : ''}
              <button class="wishlist-drawer__remove" type="button" data-wishlist-remove data-index="${index}" aria-label="${escapeAttribute((strings.removeTitle || 'Remove [title]').replace('[title]', title))}">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                  <path d="M3 6h18"></path>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </article>
      `;
    }).join('');
  };

  const setDrawerLoading = (loading) => {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;

    drawer.classList.toggle('is-loading', loading);
    drawer.querySelector('.drawer__inner')?.setAttribute('aria-busy', loading ? 'true' : 'false');

    const loadingElement = drawer.querySelector('[data-wishlist-loading]');
    if (loadingElement) loadingElement.hidden = !loading;

    drawer.querySelectorAll('[data-wishlist-cart-add-all]').forEach((button) => {
      button.disabled = loading || !state.items.some((item) => item.variantId);
    });
  };

  const sync = async ({ showLoading = false } = {}) => {
    if (showLoading) setDrawerLoading(true);

    try {
      if (state.ready) {
        state.items = normalizeItems(await callSdk('get'));
      }
    } catch (error) {
      debug('wishlist refresh failed', error);
    } finally {
      updateCounts();
      updateButtons();
      renderDrawer();
      if (showLoading) setDrawerLoading(false);
    }
  };

  const setLoading = (button, loading) => {
    button.classList.toggle('is-loading', loading);
    button.disabled = loading;
  };

  const setButtonLoading = (button, loading) => {
    button.classList.toggle('loading', loading);
    button.disabled = loading;
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
  };

  const toggleItem = async (button) => {
    if (state.pending.has(button)) return;
    if (!state.ready) {
      toast('error', strings.error || 'Wishlist is not ready');
      return;
    }

    const payload = getPayload(button);
    const existing = state.items.find((item) => sameProduct(item, payload));
    const wasActive = Boolean(existing);

    state.pending.add(button);
    setLoading(button, true);

    try {
      if (wasActive) {
        await callSdk('remove', { ...payload, ...existing.raw, item: existing.raw });
        toast('heart-crack', strings.removed || 'Removed from wishlist', payload.title);
      } else {
        await callSdk('add', payload);
        toast('heart', strings.added || 'Added to wishlist', payload.title);
      }

      await sync();
    } catch (error) {
      console.error('[wishlist] Unable to update wishlist', error);
      toast('error', strings.error || 'Wishlist error');
      updateButtons();
    } finally {
      state.pending.delete(button);
      setLoading(button, false);
    }
  };

  const bindButtons = (scope = document) => {
    scope.querySelectorAll?.(BUTTON_SELECTOR).forEach((button) => {
      if (state.buttons.has(button)) return;
      state.buttons.add(button);
      button.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleItem(button);
      });
    });

    updateButtons();
  };

  const neutralizeLegacyUi = (scope = document) => {
    const elements = [
      ...(scope.matches?.(LEGACY_UI_SELECTOR) ? [scope] : []),
      ...Array.from(scope.querySelectorAll?.(LEGACY_UI_SELECTOR) || []),
    ];

    elements.forEach((element) => {
      if (element.matches?.(BUTTON_SELECTOR)) return;

      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.style.setProperty('display', 'none', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
      element.querySelectorAll?.('button, a, input, select, textarea').forEach((control) => {
        control.disabled = true;
        control.tabIndex = -1;
        control.setAttribute('aria-hidden', 'true');
      });
    });
  };

  const addToCart = async (item, trigger) => {
    if (!item?.variantId || trigger.classList.contains('loading')) return;
    setButtonLoading(trigger, true);

    try {
      const cart = document.querySelector('cart-drawer');
      const body = {
        id: item.variantId,
        quantity: 1,
      };

      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      }).then((res) => res.json());

      if (response.status) throw new Error(response.description || strings.error);

      await refreshCartDrawer(cart, response);
    } catch (error) {
      console.error('[wishlist] Unable to add wishlist item to cart', error);
      toast('error', strings.error || 'Wishlist error');
    } finally {
      setButtonLoading(trigger, false);
    }
  };

  const refreshCartDrawer = async (cart, response) => {
    if (!cart) {
      window.location = window.routes.cart_url;
      return;
    }

    document.querySelector(DRAWER_SELECTOR)?.close();

    if (response?.sections) {
      cart.renderContents(response);
      return;
    }

    const sectionIds = cart.getSectionsToRender().map((section) => section.id).join(',');
    const sectionsUrl = `${window.routes.cart_url}?sections=${encodeURIComponent(sectionIds)}&sections_url=${encodeURIComponent(window.location.pathname)}`;
    const sections = await fetch(sectionsUrl).then((res) => res.json());

    cart.renderContents({
      id: response?.id,
      sections,
    });
  };

  const addAllToCart = async (trigger) => {
    const items = state.items
      .filter((item) => item.variantId)
      .map((item) => ({
        id: item.variantId,
        quantity: 1,
      }));

    if (!items.length || trigger.classList.contains('loading')) return;

    setButtonLoading(trigger, true);

    try {
      const cart = document.querySelector('cart-drawer');
      const body = { items };

      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      }).then((res) => res.json());

      if (response.status) throw new Error(response.description || strings.error);

      await refreshCartDrawer(cart, response);
    } catch (error) {
      console.error('[wishlist] Unable to add all wishlist items to cart', error);
      toast('error', strings.error || 'Wishlist error');
    } finally {
      setButtonLoading(trigger, false);
    }
  };

  class WishlistDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;
    }

    connectedCallback() {
      this.querySelector('[data-wishlist-close]')?.addEventListener('click', () => this.close());
      this.querySelector('#WishlistDrawer-Overlay')?.addEventListener('click', () => this.close());
      this.querySelector('[data-wishlist-cart-add-all]')?.addEventListener('click', (event) => {
        event.preventDefault();
        addAllToCart(event.currentTarget);
      });
      this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
      this.addEventListener('click', (event) => {
        if (!event.target.closest('.wishlist-drawer__inner')) {
          event.preventDefault();
          this.close();
          return;
        }

        const removeButton = event.target.closest('[data-wishlist-remove]');
        const addButton = event.target.closest('[data-wishlist-cart-add]');
        const addAllButton = event.target.closest('[data-wishlist-cart-add-all]');

        if (addAllButton) {
          event.preventDefault();
          return;
        }

        if (removeButton) {
          event.preventDefault();
          const item = state.items[Number(removeButton.dataset.index)];
          if (item) this.removeItem(item, removeButton);
          return;
        }

        if (addButton) {
          event.preventDefault();
          const item = state.items[Number(addButton.dataset.index)];
          addToCart(item, addButton);
        }
      });
    }

    async open(triggeredBy) {
      this.activeElement = triggeredBy || document.activeElement;
      this.addEventListener('transitionend', () => {
        trapFocus(this, this.querySelector('.drawer__inner') || this);
      }, { once: true });

      this.classList.add('animate', 'active');
      if (!this.bodyScrollLocked) {
        lockBodyScroll();
        this.bodyScrollLocked = true;
      }
      await sync({ showLoading: true });
    }

    close() {
      this.classList.remove('active');
      if (this.bodyScrollLocked) {
        unlockBodyScroll();
        this.bodyScrollLocked = false;
      }
      removeTrapFocus(this.activeElement);
    }

    async removeItem(item, trigger) {
      trigger.disabled = true;
      trigger.classList.add('is-loading');

      try {
        await callSdk('remove', { ...item, ...item.raw, item: item.raw });
        toast('heart-crack', strings.removed || 'Removed from wishlist', item.title);
        await sync();
      } catch (error) {
        console.error('[wishlist] Unable to remove drawer item', error);
        toast('error', strings.error || 'Wishlist error');
      } finally {
        trigger.disabled = false;
        trigger.classList.remove('is-loading');
      }
    }
  }

  const bindHeader = () => {
    document.querySelectorAll(HEADER_SELECTOR).forEach((trigger) => {
      if (trigger.dataset.wishlistBound === 'true') return;
      trigger.dataset.wishlistBound = 'true';
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        document.querySelector(DRAWER_SELECTOR)?.open(trigger);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          document.querySelector(DRAWER_SELECTOR)?.open(trigger);
        }
      });
    });
  };

  const exposeApi = () => {
    window.djadeeWishlist = {
      getItems: () => [...state.items],
      isWishlisted: (productId, variantId) => state.items.some((item) => sameProduct(item, { productId, variantId })),
      addItem: async (productData) => {
        await callSdk('add', getPayload(productData));
        await sync();
      },
      removeItem: async (productData) => {
        await callSdk('remove', getPayload(productData));
        await sync();
      },
      refresh: sync,
      getSdk: () => state.sdk,
    };
  };

  const onReady = async (event) => {
    state.sdk = getSdkCandidates(event)[0] || null;
    state.ready = Boolean(state.sdk);

    debug('sdk ready', state.sdk);
    exposeApi();
    await sync();
  };

  const init = () => {
    if (!customElements.get('wishlist-drawer')) customElements.define('wishlist-drawer', WishlistDrawer);
    neutralizeLegacyUi();
    bindButtons();
    bindHeader();
    exposeApi();

    document.addEventListener(READY_EVENT, onReady);
    document.addEventListener('shopify:section:load', (event) => {
      neutralizeLegacyUi(event.target);
      bindButtons(event.target);
      bindHeader();
    });

    const existingSdk = getSdkCandidates()[0];
    if (existingSdk) {
      onReady({ detail: existingSdk });
    }

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            neutralizeLegacyUi(node);
            bindButtons(node);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
